import { useCallback } from 'react';
import { useSearchParams } from 'react-router';
import type {
  AgentMinerSessionListParams,
  AgentSessionLabel,
} from '../../../../api/agent-miner-client/agent-miner-client.type';
import { AgentSource } from '../../../../api/events-client/events-client.type';
import { readFiltersFromSearch, resolveStartEnd, setOrDelete } from './session-filters.helpers';
import {
  DateRangePreset,
  DEFAULT_DATE_RANGE,
  RESET_KEYS,
  SessionsFiltersValue,
  type UseSessionsFilters,
} from './session-filters.type';

/**
 * URL-synced filter state for the Agent Miner sessions list.
 *
 * Mirrors the `useAdherenceLogsFilters` pattern: state is derived from the
 * current URL every render and setters write back via `useSearchParams`.
 * Every filter change resets `page=1` so pagination stays consistent.
 *
 * @returns Filter state, individual setters, and a `reset()` that clears
 *          everything and returns to the default (last 30 days, page 1).
 */
const useSessionsFilters = (): UseSessionsFilters => {
  const [searchParams, setSearchParams] = useSearchParams();
  const value = readFiltersFromSearch(searchParams);

  const update = useCallback(
    (mutator: (next: URLSearchParams) => void) => {
      const next = new URLSearchParams(searchParams);
      mutator(next);
      next.set('page', '1');
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const setQ = useCallback((next: string) => update((p) => setOrDelete(p, 'q', next)), [update]);
  const setSources = useCallback(
    (next: AgentSource[]) => update((p) => setOrDelete(p, 'source', next.join(','))),
    [update],
  );
  const setUserIds = useCallback(
    (next: string[]) => update((p) => setOrDelete(p, 'user_id', next.join(','))),
    [update],
  );
  const setLabels = useCallback(
    (next: AgentSessionLabel[]) => update((p) => setOrDelete(p, 'label', next.join(','))),
    [update],
  );
  const setDateRange = useCallback(
    (next: DateRangePreset) =>
      update((p) => {
        setOrDelete(p, 'started', next === DEFAULT_DATE_RANGE ? '' : next);
        if (next !== 'custom') {
          p.delete('start_date');
          p.delete('end_date');
        }
      }),
    [update],
  );
  const setCustomRange = useCallback(
    (start: string | null, end: string | null) =>
      update((p) => {
        setOrDelete(p, 'start_date', start);
        setOrDelete(p, 'end_date', end);
        p.set('started', 'custom');
      }),
    [update],
  );
  const setHasTask = useCallback(
    (next: boolean | null) => update((p) => setOrDelete(p, 'has_task', next == null ? '' : String(next))),
    [update],
  );
  const setHideEmpty = useCallback(
    (next: boolean) => update((p) => setOrDelete(p, 'hide_empty', next ? '' : 'false')),
    [update],
  );
  const setModelFamilies = useCallback(
    (next: string[]) => update((p) => setOrDelete(p, 'model_family', next.join(','))),
    [update],
  );

  const reset = useCallback(() => {
    update((p) => {
      for (const key of RESET_KEYS) p.delete(key);
    });
  }, [update]);

  return {
    value,
    setQ,
    setSources,
    setUserIds,
    setLabels,
    setDateRange,
    setCustomRange,
    setHasTask,
    setHideEmpty,
    setModelFamilies,
    reset,
  };
};

/**
 * Build the query-string portion of the list-hook params from the current
 * filter value + pagination cursor.
 */
const buildListParams = (
  filters: SessionsFiltersValue,
  overrides: { organization_id: string; page: number; page_size: number; ordering: string },
): AgentMinerSessionListParams => {
  const { start, end } = resolveStartEnd(filters);
  return {
    organization_id: overrides.organization_id,
    page: overrides.page,
    page_size: overrides.page_size,
    q: filters.q || undefined,
    source: filters.sources.length > 0 ? filters.sources : undefined,
    user_id: filters.userIds.length > 0 ? filters.userIds : undefined,
    label: filters.labels.length > 0 ? filters.labels : undefined,
    start_date: start,
    end_date: end,
    has_task: filters.hasTask ?? undefined,
    min_turns: filters.hideEmpty ? 1 : undefined,
    model_family: filters.modelFamilies.length > 0 ? filters.modelFamilies : undefined,
    ordering: overrides.ordering,
  };
};

export { buildListParams, useSessionsFilters };
